import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Bell, Check, CheckCheck, ExternalLink, X } from 'lucide-react';
import { Button } from './ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const NotificationsDropdown = ({ isOpen, onClose, onUpgradeClick }) => {
  const navigate = useNavigate();
  const { isPro, token } = useAuth();
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isPro || !token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isPro, token]);
  
  useEffect(() => {
    if (isOpen && isPro) {
      fetchNotifications();
    }
  }, [isOpen, isPro, fetchNotifications]);
  
  // Mark single as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API_BASE}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.post(`${API_BASE}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };
  
  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.readAt) {
      markAsRead(notification.id);
    }
    if (notification.symbol) {
      navigate(`/app/stock/${notification.symbol}`);
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100]"
      onClick={onClose}
    >
      <div 
        className="absolute right-4 top-16 w-80 max-h-96 overflow-hidden rounded-xl shadow-2xl z-[101]"
        style={{
          backgroundColor: '#18181b',
          border: '1px solid rgba(255,255,255,0.08)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="px-4 py-3 flex items-center justify-between border-b"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <h3 className="font-medium" style={{ color: '#f4f4f5' }}>
            Notifications
          </h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs flex items-center gap-1 px-2 py-1 rounded"
                style={{ color: '#a78bfa' }}
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
            )}
            <button onClick={onClose} style={{ color: '#71717a' }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto max-h-72">
          {!isPro ? (
            <div className="p-6 text-center">
              <Bell className="w-10 h-10 mx-auto mb-3" style={{ color: '#3f3f46' }} />
              <p className="text-sm mb-3" style={{ color: '#71717a' }}>
                Notifications are a Pro feature
              </p>
              <Button size="sm" onClick={onUpgradeClick}>
                Upgrade to Pro
              </Button>
            </div>
          ) : loading ? (
            <div className="p-6 text-center" style={{ color: '#71717a' }}>
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-10 h-10 mx-auto mb-3" style={{ color: '#3f3f46' }} />
              <p className="text-sm" style={{ color: '#71717a' }}>
                No notifications yet
              </p>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="px-4 py-3 border-b cursor-pointer transition-colors hover:bg-white/5"
                  style={{ 
                    borderColor: 'rgba(255,255,255,0.04)',
                    backgroundColor: notification.readAt ? 'transparent' : 'rgba(124,92,255,0.05)'
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                      style={{ 
                        backgroundColor: notification.readAt ? 'transparent' : '#a78bfa'
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#f4f4f5' }}>
                        {notification.title}
                      </p>
                      <p className="text-xs mt-0.5 line-clamp-2" style={{ color: '#71717a' }}>
                        {notification.body}
                      </p>
                      <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                        {new Date(notification.createdAt).toLocaleString()}
                      </p>
                    </div>
                    {notification.symbol && (
                      <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: '#52525b' }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {isPro && notifications.length > 0 && (
          <div 
            className="px-4 py-2 border-t text-center"
            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <button
              onClick={() => { navigate('/app/alerts'); onClose(); }}
              className="text-xs"
              style={{ color: '#a78bfa' }}
            >
              Manage Alerts
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Bell icon with badge
export const NotificationBell = ({ onClick, isPro }) => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Fetch unread count periodically
  useEffect(() => {
    if (!isPro || !token) return;
    
    const fetchCount = async () => {
      try {
        const response = await axios.get(`${API_BASE}/notifications?unreadOnly=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data.unreadCount || 0);
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // Every minute
    
    return () => clearInterval(interval);
  }, [isPro, token]);
  
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-lg transition-colors"
      style={{ color: '#a1a1aa' }}
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span 
          className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[10px] font-medium flex items-center justify-center"
          style={{ 
            backgroundColor: '#a78bfa',
            color: '#fff'
          }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
};

export default NotificationsDropdown;
